import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/pfc";
import YouTubeEmbed from "@/components/YouTubeEmbed";
import { Camera, Film, Music, Heart, Sparkles, Mic2, Briefcase, Video, ArrowRight, Check } from "lucide-react";

const SERVICE_ICONS = {
  "HD Video Recording": Camera,
  "Professional Editing": Video,
  "Music Videos": Music,
  "Short Films": Film,
  "Documentaries": Film,
  "Promotional Commercials": Briefcase,
  "Party & Event Coverage": Sparkles,
  "Weddings": Heart,
  "Fashion Shows": Mic2,
};

export default function Services() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/services/epic-scope").then((r) => setData(r.data));
  }, []);

  if (!data) return <div className="min-h-[50vh] grid place-items-center text-white/40 label">Loading…</div>;

  return (
    <div data-testid="services-page">
      {/* HERO */}
      <section className="relative border-b border-white/5 py-20 overflow-hidden splatter-red grain">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 relative grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="label text-pfc-gold mb-3">Production · Powered by Epic Scope Film</div>
            <h1 className="font-display text-3xl sm:text-5xl lg:text-6xl text-white uppercase leading-[0.9]">
              Cinema-grade <span className="gold-text">production</span> for the campaigns that get seen.
            </h1>
            <p className="text-white/70 mt-6 font-body text-lg max-w-xl">
              From music videos to feature animation, Epic Scope Film is the in-house production partner behind PFC’s film slate — and yours.
            </p>
            <div className="flex gap-3 mt-8 flex-wrap">
              <Link to="/contact" className="font-sub uppercase tracking-[0.25em] text-sm px-8 py-4 bg-pfc-gold text-ink-900 hover:bg-pfc-gold-bright transition" data-testid="services-book-btn">
                Book Production
              </Link>
              <Link to="/film" className="font-sub uppercase tracking-[0.25em] text-sm px-8 py-4 border border-white/20 text-white hover:border-pfc-gold hover:text-pfc-gold transition" data-testid="services-film-btn">
                See Our Slate
              </Link>
            </div>
          </div>
          <div className="relative flex items-center justify-center">
            <div className="absolute inset-0 bg-gradient-radial from-pfc-red/30 via-transparent to-transparent blur-3xl" />
            <img src={data.logo} alt="Epic Scope Film" className="relative w-72 h-72 lg:w-96 lg:h-96 object-contain" data-testid="epic-scope-logo" />
          </div>
        </div>
      </section>

      {/* SERVICES GRID */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-10 py-20" data-testid="services-grid-section">
        <div className="mb-10">
          <div className="label text-pfc-gold mb-3">Featured Reel</div>
          <h2 className="font-display text-3xl lg:text-4xl text-white uppercase leading-none">Latest <span className="gold-text">Production</span>.</h2>
        </div>
        <div className="mb-14 pfc-card overflow-hidden" data-testid="epicscope-featured-film">
          <YouTubeEmbed videoId="oLrpwiJ4gT4" title="Epicscope · Featured Film" />
        </div>
        <div className="mb-12">
          <div className="label text-pfc-gold mb-3">What We Do</div>
          <h2 className="font-display text-3xl lg:text-4xl text-white uppercase leading-none">The Full <span className="gold-text">Stack</span>.</h2>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-5" data-testid="services-grid">
          {data.services.map((s, i) => {
            const Icon = SERVICE_ICONS[s] || Video;
            return (
              <div key={s} className="pfc-card p-8" data-testid={`service-${i}`}>
                <Icon className="text-pfc-gold mb-4" size={28} />
                <div className="font-sub uppercase tracking-[0.15em] text-white">{s}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CLIENT ROSTER */}
      <section className="bg-ink-800 border-y border-white/5 py-20" data-testid="services-clients-section">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
          <div className="mb-10">
            <div className="label text-pfc-gold mb-3">Selected Clients</div>
            <h2 className="font-display text-3xl lg:text-4xl text-white uppercase leading-none">Trusted by <span className="gold-text">the names</span> in the work.</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="services-clients">
            {data.clients.map((c) => (
              <div key={c} className="flex items-start gap-3 p-5 border border-white/10 hover:border-pfc-gold/40 transition">
                <Check className="text-pfc-gold shrink-0 mt-0.5" size={18} />
                <span className="text-white/85 font-body text-sm">{c}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1100px] mx-auto px-5 lg:px-10 py-24 text-center">
        <div className="label text-pfc-gold mb-3">Let’s Build</div>
        <h2 className="font-display text-3xl lg:text-4xl text-white uppercase leading-[0.95]">
          Your next project<br /> deserves a <span className="gold-text">camera that knows what it’s doing.</span>
        </h2>
        <Link to="/contact" className="inline-flex items-center gap-2 mt-10 font-sub uppercase tracking-[0.25em] text-sm px-10 py-5 bg-pfc-gold text-ink-900 hover:bg-pfc-gold-bright transition" data-testid="services-cta-btn">
          Start a Production Brief <ArrowRight size={16} />
        </Link>
      </section>
    </div>
  );
}
